import { roundToCents } from "../../../../utils/roundToCents";
import { AssetExpensePosition, AssetExpensesTab, SaveAssetExpenses } from "../../../api/ClientFileApi";
import { AssetExpensesFormValues, ExpensePositionsFormValue } from "./ExpensesView.types";

const ALL_POSITIONS = Object.values(AssetExpensePosition);

function emptyPositions(): ExpensePositionsFormValue {
  return Object.fromEntries(
    ALL_POSITIONS.map((position) => [position, { amount: null, debtorShare: null }])
  ) as ExpensePositionsFormValue;
}

export function fromExpensesResponse(data: AssetExpensesTab | undefined): AssetExpensesFormValues {
  const positions = emptyPositions();
  for (const expense of data?.expenses ?? []) {
    positions[expense.position] = { amount: expense.amount ?? null, debtorShare: expense.debtorShare };
  }

  return {
    hasIllnessExtraCosts: data?.hasIllnessExtraCosts ?? null,
    illnessExtraCostAmount: data?.illnessExtraCostAmount ?? null,
    paysCostsAlone: data?.paysCostsAlone ?? null,
    costSharingExplanation: data?.costSharingExplanation ?? null,
    expenseRemarks: data?.expenseRemarks ?? null,
    positions,
    expenseProofs: [],
    documentMoves: [],
  };
}

export function toExpensesRequest(values: AssetExpensesFormValues): SaveAssetExpenses {
  const paysCostsAlone = values.paysCostsAlone === true;
  const hasIllnessExtraCosts = values.hasIllnessExtraCosts === true;

  return {
    hasIllnessExtraCosts: values.hasIllnessExtraCosts,
    illnessExtraCostAmount: hasIllnessExtraCosts ? values.illnessExtraCostAmount : null,
    paysCostsAlone: values.paysCostsAlone,
    costSharingExplanation: values.paysCostsAlone === false ? values.costSharingExplanation : null,
    expenseRemarks: values.expenseRemarks,
    expenses: ALL_POSITIONS.map((position) => ({
      position,
      amount: paysCostsAlone ? null : values.positions[position].amount,
      debtorShare: values.positions[position].debtorShare,
    })).filter((expense) => (expense.amount ?? 0) !== 0 || (expense.debtorShare ?? 0) !== 0),
  };
}

/** The live total of the client's shares plus the maintenance; the illness extra costs never count. */
export function computeTotalExpenses(positions: ExpensePositionsFormValue, maintenanceAmount: number): number {
  const shares = ALL_POSITIONS.reduce((sum, position) => sum + (positions[position].debtorShare ?? 0), 0);
  return roundToCents(shares + maintenanceAmount);
}

/**
 * Carries the amounts over when the cost-sharing answer flips, the way the legacy tab reads them back:
 * alone, the single column shows the former full amount; shared, the full amount starts at the share.
 */
export function switchCostSharing(
  positions: ExpensePositionsFormValue,
  paysCostsAlone: boolean
): ExpensePositionsFormValue {
  return Object.fromEntries(
    ALL_POSITIONS.map((position) => {
      const { amount, debtorShare } = positions[position];
      return [
        position,
        paysCostsAlone ? { amount: null, debtorShare: amount ?? debtorShare } : { amount: debtorShare, debtorShare },
      ];
    })
  ) as ExpensePositionsFormValue;
}
